import parsing from './parsing.png'

const content = `<p>The Abstract factory design pattern allows to define types of objects that go together to form families, and to ensure that all the objects created in a context belong to the same family.</p>

<p>It is not to be confused with the <a href="/factory-method">Factory method</a> design pattern (although Abstract factory generalizes Factory method as we will see) nor with what people just tend to call <a href="/factory">Factory</a>.</p>

<h2 id="Example">Example</h2>

<p>Let’s take an example to illustrate. To parse a piece of source code and produce an AST, parsers proceed in two steps:</p>

<ul><li>lexing: identifying all the successive symbols, called “tokens”, that the code contains</li><li>AST building: using the grammar of the language to structure the relations of the tokens into a tree</li></ul>

<p>As an example, consider the following code expression <code>a = b + c</code>, that gets through a lexer and an AST builder:</p>

<img src=${parsing} alt="parsing" />

<p>Lexers and AST builders are specific to languages: there are lexers and AST builders for Python, C++, SQL, etc.</p>

<p>Let’s assume we have classes that represent those lexers and parsers. Here is a Python lexer:</p>

<pre><code class="language-ruby">class PythonLexer
  def tokenize(source_code)
    # return Python tokens
  end
end</code></pre>

<p>And a Python AST builder:</p>

<pre><code class="language-ruby">class PythonASTBuilder
  def build_ast(tokens)
    # return Python AST
  end
end</code></pre>

<p>A C++ lexer:</p>

<pre><code class="language-ruby">class CppLexer
  def tokenize(source_code)
    # return C++ tokens
  end
end</code></pre>

<p>And a C++ AST builder:</p>

<pre><code class="language-ruby">class CppASTBuilder
  def build_ast(tokens)
    # return C++ AST
  end
end</code></pre>

<p>and so on for other languages.</p>

<p>We’d like to write the code of a parser, that uses the lexer and the AST builder of a given language to transform source code into an AST.</p>

<p>How can we make sure we always use consistent lexers and AST builders of the same language?</p>

<h2 id="Abstract-factory">Abstract factory</h2>

<p>Abstract factory allows to group objects in families and ensure that all the objects created in a context are of the same family. In our example, each language represents a family of objects, made of a lexer and an AST builder.</p>

<p>Here is a class that handles the Python family:</p>

<pre><code class="language-ruby">class PythonFactory
  def create_lexer
    PythonLexer.new
  end
  
  def create_ast_builder
    PythonASTBuilder.new
  end
end</code></pre>

<p>Here is the class that handles the C++ family:</p>

<pre><code class="language-ruby">class CppFactory
  def create_lexer
    CppLexer.new
  end
  
  def create_ast_builder
    CppASTBuilder.new
  end
end</code></pre>

<p>We can now write the code of the parser that relies on those classes, by passing a factory that can be of any type (hence the name “Abstract factory”) thus using <a href="/polymorphism">Polymorphism</a>:</p>

<pre><code class="language-ruby">def parse(source_code, language_factory)
  lexer = language_factory.create_lexer
  ast_builder = language_factory.create_ast_builder
  
  tokens = lexer.tokenize(source_code)
  ast_builder.build_ast(tokens)
end</code></pre>

<p>We can call it this way to parse Python code:</p>

<pre><code class="language-ruby">ast = parse("print('Hello, world!')", PythonFactory.new)</code></pre>

<p>And this way to parse C++ code:</p>

<pre><code class="language-ruby">ast = parse('std::cout &lt;&lt; "Hello, world!\n"', CppFactory.new)</code></pre>

<p>With Abstract factory we’re sure never to use a lexer and an AST builder that are not of the same language in the same parsing operation.</p>

<p>What’s more, the code of the parser has <a href="/low-coupling">Low coupling</a> with the specific languages: if any language rule changes, or if new languages are added, the code of the parser doesn’t have to change.</p>

<p>Note that Abstract factory is very similar to Factory method, as both rely on polymorphic classes that choose types of objects to instantiate. The different is that Factory method instantiates one object whereas Abstract factory instantiates a family of objects. Abstract factory can be seen as a generalization of Factory method.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Abstract factory consist in writing classes that create family of objects to be used together</li><li>It uses Polymorphism as the user of the factory doesn’t know its particular family</li><li>It generalizes Factory method that creates only one object and not a family</li></ul>
</div>`

export default content
